import { useParams, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useCoinDetail, useMarketChart } from '../hooks/useCoins';
import { useSettings } from '../hooks/useSettings';
import { Card, CardContent, CardHeader } from '../components/ui/Card';
import { Skeleton } from '../components/ui/Skeleton';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { formatPrice, formatNumber, formatPercentage, getChangeClass, formatDate } from '../lib/utils';
import { ArrowLeft, TrendingUp, TrendingDown, Activity, Calendar, Globe, Users, Code, Star } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

// Force HMR update on file change
if (import.meta.hot) {
  import.meta.hot.accept();
}

const timeRanges = [
  { label: '24H', days: 1 },
  { label: '7D', days: 7 },
  { label: '30D', days: 30 },
  { label: '90D', days: 90 },
  { label: '1Y', days: 365 },
];

export default function CoinDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [selectedRange, setSelectedRange] = useState(7);

  const { coin, loading: coinLoading, error: coinError } = useCoinDetail(id || '', settings.currency);
  const { chart, loading: chartLoading, error: chartError } = useMarketChart(
    id || '',
    selectedRange,
    settings.currency
  );

  if (!id) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <h2 className="text-xl font-semibold text-black dark:text-white">
          Coin not found
        </h2>
        <button
          onClick={() => navigate('/')}
          className="mt-4 px-4 py-2 bg-black hover:bg-gray-900 text-white dark:bg-white dark:hover:bg-gray-200 dark:text-black rounded-lg transition-colors"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  if (coinLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-12 w-12 rounded-full" />
          <div className="flex-1">
            <Skeleton className="h-8 w-48 mb-2" />
            <Skeleton className="h-4 w-24" />
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-64 lg:col-span-2" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (coinError || !coin) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="bg-red-100 dark:bg-red-900/30 p-4 rounded-full mb-4">
          <Activity className="h-8 w-8 text-red-600 dark:text-red-400" />
        </div>
        <h2 className="text-xl font-semibold text-black dark:text-white mb-2">
          Unable to load coin details
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-4">{coinError}</p>
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 bg-black hover:bg-gray-900 text-white dark:bg-white dark:hover:bg-gray-200 dark:text-black rounded-lg transition-colors"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  const priceChange24h = coin.price_change_percentage_24h;
  const isPositive = priceChange24h >= 0;

  // Generate fallback chart data if chart is empty or missing
  let chartData = chart?.prices?.map(([timestamp, price]) => ({
    date: timestamp,
    price,
  })) || [];
  
  // If chart has mock data with placeholder prices, scale to actual coin price
  if (chartData.length > 0 && chartData[0].price === 1) {
    const basePrice = coin.current_price;
    chartData = chartData.map(item => ({
      date: item.date,
      price: item.price * basePrice,
    }));
  }
  
  // If no chart data, generate mock data based on current price
  if (chartData.length === 0 && coin.current_price) {
    const now = Date.now();
    const interval = selectedRange <= 1 ? 3600000 : 86400000; // 1 hour or 1 day
    const points = selectedRange <= 1 ? 24 : selectedRange;
    const basePrice = coin.current_price;
    
    chartData = [];
    for (let i = points; i >= 0; i--) {
      const timestamp = now - (i * interval);
      // Generate variations around current price
      const variation = (Math.random() - 0.5) * 0.05; // ±2.5% variation
      const price = basePrice * (1 + variation);
      chartData.push({ date: timestamp, price });
    }
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/')}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
        >
          <ArrowLeft className="h-6 w-6 text-gray-600 dark:text-gray-400" />
        </button>
        <img src={coin.image} alt={coin.name} className="h-12 w-12 rounded-full" />
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-black dark:text-white truncate">
            {coin.name}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 uppercase">
            {coin.symbol} • Rank #{coin.market_cap_rank}
          </p>
        </div>
        <Button variant="outline" size="sm">
          <Star className="h-4 w-4" />
          Watchlist
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Current Price</p>
                <p className="text-3xl font-bold text-black dark:text-white">
                  {formatPrice(coin.current_price, settings.currency)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {isPositive ? (
                  <TrendingUp className="h-6 w-6 text-green-600 dark:text-green-400" />
                ) : (
                  <TrendingDown className="h-6 w-6 text-red-600 dark:text-red-400" />
                )}
                <span className={`text-xl font-semibold ${getChangeClass(priceChange24h)}`}>
                  {formatPercentage(priceChange24h)}
                </span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-4 flex gap-2">
              {timeRanges.map((range) => (
                <button
                  key={range.days}
                  onClick={() => setSelectedRange(range.days)}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    selectedRange === range.days
                      ? 'bg-black dark:bg-white text-white dark:text-black'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {range.label}
                </button>
              ))}
            </div>
            <div className="h-64">
              {chartLoading && chartData.length === 0 ? (
                <Skeleton className="h-full w-full" />
              ) : chartData.length === 0 ? (
                <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                  <div className="text-center">
                    <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>Chart data temporarily unavailable</p>
                    <p className="text-xs mt-1">Please try again later</p>
                  </div>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.2)" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(timestamp) => formatDate(timestamp)}
                      stroke="currentColor"
                      tick={{ fill: 'currentColor', fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                      tickCount={5}
                      minTickGap={20}
                    />
                    <YAxis
                      dataKey="price"
                      domain={['dataMin - dataMin*0.02', 'dataMax + dataMax*0.02']}
                      stroke="currentColor"
                      tick={{ fill: 'currentColor', fontSize: 12 }}
                      tickLine={false}
                      axisLine={false}
                      tickCount={5}
                      width={80}
                      tickFormatter={(value) => {
                        // Format large numbers more compactly
                        if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                        if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
                        return value.toFixed(2);
                      }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgba(0, 0, 0, 0.9)',
                        border: '1px solid rgba(107, 114, 128, 0.3)',
                        borderRadius: '8px',
                        color: 'white',
                      }}
                      formatter={(value: number) => [formatPrice(value, settings.currency), 'Price']}
                      labelFormatter={(label) => formatDate(label)}
                      cursor={{ stroke: 'rgba(107, 114, 128, 0.5)', strokeWidth: 1 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="price"
                      stroke="url(#gradient)"
                      strokeWidth={3}
                      dot={false}
                      activeDot={{ r: 6, strokeWidth: 2 }}
                      animationDuration={300}
                    />
                    <defs>
                      <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#000000" />
                        <stop offset="100%" stopColor="#6b7280" />
                      </linearGradient>
                    </defs>
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <h3 className="font-semibold text-black dark:text-white">Market Stats</h3>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500 dark:text-gray-400">Market Cap</span>
                <span className="font-medium text-black dark:text-white">
                  {formatNumber(coin.market_cap)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500 dark:text-gray-400">24h Volume</span>
                <span className="font-medium text-black dark:text-white">
                  {formatNumber(coin.total_volume)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500 dark:text-gray-400">Circulating Supply</span>
                <span className="font-medium text-black dark:text-white">
                  {formatNumber(coin.circulating_supply)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500 dark:text-gray-400">24h High</span>
                <span className="font-medium text-green-600 dark:text-green-400">
                  {formatPrice(coin.high_24h, settings.currency)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500 dark:text-gray-400">24h Low</span>
                <span className="font-medium text-red-600 dark:text-red-400">
                  {formatPrice(coin.low_24h, settings.currency)}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <h3 className="font-semibold text-black dark:text-white">Community</h3>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Twitter
                </span>
                <span className="font-medium text-black dark:text-white">
                  {formatNumber(coin.community_data?.twitter_followers || 0)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  Reddit
                </span>
                <span className="font-medium text-black dark:text-white">
                  {formatNumber(coin.community_data?.reddit_subscribers || 0)}
                </span>
              </div>
            </CardContent>
          </Card>

          {coin.genesis_date && (
            <Card>
              <CardHeader>
                <h3 className="font-semibold text-black dark:text-white">Info</h3>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <Calendar className="h-4 w-4" />
                  <span>Launched: {coin.genesis_date}</span>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {coin.description?.en && (
        <Card>
          <CardHeader>
            <h3 className="font-semibold text-black dark:text-white">About</h3>
          </CardHeader>
          <CardContent>
            <div
              className="prose prose-gray dark:prose-invert max-w-none text-sm text-gray-600 dark:text-gray-400"
              dangerouslySetInnerHTML={{
                __html: coin.description.en
                  .replace(/<a/g, '<a target="_blank" rel="noopener noreferrer"')
                  .slice(0, 1000) + '...',
              }}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}